/**
 * pages/DocumentsPage.tsx — CLI-451
 * Список документов пациента (DocumentFile). Read-only. Скачивание PDF.
 */
import { useQuery } from '@tanstack/react-query';
import { getDocuments } from '../services/patient.service';
import { useAuthStore } from '../store/auth.store';

function fileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} Б`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} КБ`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} МБ`;
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString('ru-RU', { day: 'numeric', month: 'long', year: 'numeric' });
}

export function DocumentsPage() {
  const { accessToken } = useAuthStore();
  const { data: docs, isLoading, isError } = useQuery({
    queryKey: ['patient-documents'],
    queryFn: getDocuments,
  });

  if (isLoading) return (
    <div className="p-4 space-y-3">
      {Array.from({ length: 4 }).map((_, i) => (
        <div key={i} className="bg-white rounded-xl h-20 animate-pulse" />
      ))}
    </div>
  );

  if (isError) return (
    <div className="p-4 text-center text-red-500">
      Ошибка загрузки документов. Попробуйте позже.
    </div>
  );

  if (!docs?.length) return (
    <div className="p-4 text-center text-gray-500 mt-12">
      <p className="text-4xl mb-3">📄</p>
      <p className="font-medium text-gray-700">Документов пока нет</p>
      <p className="text-sm mt-1">Здесь появятся ваши медицинские документы и справки</p>
    </div>
  );

  return (
    <div className="p-4 space-y-3">
      <h2 className="text-lg font-bold text-gray-800">Мои документы</h2>
      {docs.map((doc) => (
        <div key={doc.id} className="bg-white rounded-xl p-4 shadow-sm border border-gray-100 flex items-center gap-3">
          <div className="text-3xl">
            {doc.mimeType === 'application/pdf' ? '📕' : '📎'}
          </div>
          <div className="flex-1 min-w-0">
            <p className="font-medium text-gray-800 text-sm truncate">{doc.originalName}</p>
            <p className="text-xs text-gray-500">{formatDate(doc.createdAt)} · {fileSize(doc.fileSizeBytes)}</p>
          </div>
          {doc.status === 'DONE' && accessToken && (
            <button
              type="button"
              className="shrink-0 bg-primary-600 hover:bg-primary-700 text-white text-xs font-medium px-3 py-1.5 rounded-lg transition"
              onClick={() => {
                // <a href> без заголовка даёт 401 — используем fetch с Bearer токеном
                void fetch(`/api/patient/files/${doc.id}/download`, {
                  headers: { Authorization: `Bearer ${accessToken}` },
                })
                  .then(async (r) => {
                    if (!r.ok) {
                      const body = await r.json().catch(() => ({})) as { error?: string };
                      const isConsent = r.status === 403;
                      throw new Error(isConsent ? 'consent_required' : (body.error ?? 'download_failed'));
                    }
                    return r.blob();
                  })
                  .then((blob) => {
                    const url = URL.createObjectURL(blob);
                    const a = document.createElement('a');
                    a.href = url;
                    a.download = doc.originalName;
                    document.body.appendChild(a);
                    a.click();
                    document.body.removeChild(a);
                    URL.revokeObjectURL(url);
                  })
                  .catch((err: unknown) => {
                    const msg = (err as { message?: string })?.message;
                    alert(msg === 'consent_required'
                      ? 'Для скачивания необходимо действующее согласие на обработку персональных данных. Обратитесь в регистратуру.'
                      : 'Не удалось скачать файл. Попробуйте позже.');
                  });
              }}
            >
              Скачать
            </button>
          )}
        </div>
      ))}
    </div>
  );
}
